// Helper de telemetria do front. Posta no backend (/api/logs/client) os
// erros capturados por ErrorBoundary, window.onerror e
// window.onunhandledrejection. Decisão da Fase 3:
//
// - Usa fetch nativo (não axios) pra evitar loop infinito: se o axios
//   interceptor estourar, ele tentaria reportar via axios e geraria mais
//   erro. Fetch puro com .catch(() => {}) engole tudo.
// - Auth obrigatória do endpoint — se não houver token no localStorage,
//   desiste silenciosamente (erro antes do login fica só no console).
// - Try/catch silencioso em volta de TUDO — telemetria nunca pode quebrar
//   a UI.

export interface ClientLogPayload {
  message: string;
  stack?: string;
  /** "error" (default) | "warn" | "info" — qualquer outro vira "info" no backend. */
  level?: 'error' | 'warn' | 'info';
  /** componentStack do React (vem do ErrorInfo no ErrorBoundary). */
  componentStack?: string;
  /** Metadados extras: source ("boundary"|"window.onerror"|"unhandledrejection"), filename, lineno, etc. */
  extra?: Record<string, unknown>;
}

const STORAGE_KEY_TOKEN = 'auth_token';
const STORAGE_KEY_CORRELATION = 'utilyhub:correlation-id';

function getCorrelationId(): string {
  try {
    let id = sessionStorage.getItem(STORAGE_KEY_CORRELATION);
    if (!id) {
      // crypto.randomUUID retorna ex.: "abc-def-...". Limpa hífens pra casar
      // com o formato Guid("N") do backend (32-hex sem separadores).
      id = (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)).replace(/-/g, '');
      sessionStorage.setItem(STORAGE_KEY_CORRELATION, id);
    }
    return id;
  } catch {
    return Math.random().toString(36).slice(2);
  }
}

/**
 * Helper canônico pra catch blocks. Faz 2 coisas:
 *  1) console.error local (devtools, log do navegador) — pra debug em DEV.
 *  2) postClientLog pro backend — telemetria centralizada (utilyhub.audit_logs).
 *
 * Substituir gradualmente console.error em catch blocks por logError.
 * Uso:
 *   catch (error) { logError('Erro ao carregar X', error); }
 */
export function logError(message: string, error: unknown, extra?: Record<string, unknown>): void {
  // Sempre console.error — mantém comportamento que devs esperam (visível
  // imediatamente no console do navegador, com stack expandido).
  console.error(message, error);

  // Best-effort telemetria. Falha silenciosa: nunca deve quebrar o catch.
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    postClientLog({
      message: `${message}: ${err.message}`,
      stack: err.stack,
      level: 'error',
      extra,
    });
  } catch {
    /* nunca propaga */
  }
}

export function postClientLog(payload: ClientLogPayload): void {
  // Tudo dentro de try/catch — promessa de "nunca quebra a UI".
  try {
    const token = localStorage.getItem(STORAGE_KEY_TOKEN);
    if (!token) return; // sem token, endpoint dá 401 — não vale a tentativa.

    const body = {
      ...payload,
      url: typeof location !== 'undefined' ? location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      level: payload.level ?? 'error',
    };

    void fetch('/api/logs/client', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Correlation-Id': getCorrelationId(),
      },
      body: JSON.stringify(body),
      // Mesmo se backend timeout/500, não queremos pendência. credentials default já é OK pra cookie/header.
      keepalive: true,
    }).catch(() => { /* silencioso */ });
  } catch {
    // Algum browser exótico pode lançar mesmo em try/catch. Engole.
  }
}
